// TeamGridBlock.jsx
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Block from '../../../components/Block/Block';
import TeammateCard from '../../../components/TeammateCard/TeammateCard';
import TeammateModal from '../../../components/TeammateModal/TeammateModal';
import Button from '../../../components/Button/Button';
import FadeIn from '../../../components/FadeIn/FadeIn';
import OnlineRecordingForm from '../../../components/OnlineRecordingForm/OnlineRecordingForm';
import Modal from '../../../components/Modal/Modal';
import PaginationControl from '../../../components/PaginationControl/PaginationControl';
import { FiSearch, FiX, FiUsers } from 'react-icons/fi';
import style from './teamgridblock.module.scss';
import { fetchCoaches } from '../../../services/api';

const TeamGridBlock = () => {
    const [isMobile, setIsMobile] = useState(false);
    const [modal, setModal] = useState();
    const [allCoaches, setAllCoaches] = useState([]);
    const [displayedCoaches, setDisplayedCoaches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageCount, setPageCount] = useState(1);
    const [pageSize] = useState(9);
    const [totalCoaches, setTotalCoaches] = useState(0);
    const [selectedCoach, setSelectedCoach] = useState(null);
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const searchInputRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        const checkScreenSize = () => {
            setIsMobile(window.innerWidth <= 1024);
        };

        checkScreenSize();
        window.addEventListener('resize', checkScreenSize);

        return () => {
            window.removeEventListener('resize', checkScreenSize);
        };
    }, []);

    const loadAllCoaches = useCallback(async () => {
        setLoading(true);
        try {
            const { data, pagination } = await fetchCoaches({
                page: 1,
                pageSize: 100
            });
            setAllCoaches(data);
            setTotalCoaches(pagination.total);
            loadPage(1);
        } catch (error) {
            console.error('Error loading coaches:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadPage = useCallback(async (page) => {
        try {
            const { data, pagination } = await fetchCoaches({
                page: page,
                pageSize: pageSize
            });
            setDisplayedCoaches(data);
            setPageCount(pagination.pageCount);
            setCurrentPage(page);
        } catch (error) {
            console.error('Error loading page:', error);
        }
    }, [pageSize]);

    useEffect(() => {
        loadAllCoaches();
    }, [loadAllCoaches]);

    useEffect(() => {
        if (currentPage > 0 && !searchTerm) {
            loadPage(currentPage);
        }
    }, [currentPage, searchTerm, loadPage]);

    const filterCoaches = useCallback((coachesArray, term) => {
        if (!term.trim()) {
            return [];
        }
        
        const lowercasedTerm = term.toLowerCase().trim();
        
        return coachesArray.filter(coach => {
            const firstName = coach.attributes?.FirstName?.toLowerCase() || '';
            const lastName = coach.attributes?.LastName?.toLowerCase() || '';
            const patronymic = coach.attributes?.PatronymicName?.toLowerCase() || '';
            const fullName = `${lastName} ${firstName} ${patronymic}`.toLowerCase();
            
            return (
                firstName.includes(lowercasedTerm) ||
                lastName.includes(lowercasedTerm) ||
                patronymic.includes(lowercasedTerm) ||
                fullName.includes(lowercasedTerm)
            );
        });
    }, []);

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        
        if (value.trim() === '') {
            setDisplayedCoaches([]);
            loadPage(1);
        } else {
            const filtered = filterCoaches(allCoaches, value);
            setDisplayedCoaches(filtered);
            setPageCount(1);
            setCurrentPage(1);
        }
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        setDisplayedCoaches([]);
        loadPage(1);
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    };

    const handleCoachClick = (coach) => {
        const coachData = {
            id: coach.id,
            ...coach.attributes,
            sport: coach.attributes?.sport,
            Image: coach.attributes?.Image,
            name: `${coach.attributes?.LastName || ''} ${coach.attributes?.FirstName || ''} ${coach.attributes?.PatronymicName || ''}`,
            LastName: coach.attributes?.LastName || '',
            FirstName: coach.attributes?.FirstName || '',
            PatronymicName: coach.attributes?.PatronymicName || '',
            Achievements: coach.attributes?.Achievements || '',
            Experience: coach.attributes?.Experience || '',
            Position: coach.attributes?.Position || '',
            Description: coach.attributes?.Description || '',
            Education: coach.attributes?.Education || '',
            Specialization: coach.attributes?.Specialization || '',
            AdditionalAchievements: coach.attributes?.AdditionalAchievements || []
        };
        setSelectedCoach(coachData);
    };

    const handleCloseCoachModal = () => {
        setSelectedCoach(null);
    };

    const prepareCoachDataForCard = (coach) => {
        return {
            id: coach.id,
            name: `${coach.attributes?.LastName || ''} ${coach.attributes?.FirstName || ''} ${coach.attributes?.PatronymicName || ''}`.trim(),
            sport: coach.attributes?.sport?.data?.attributes?.Title || coach.attributes?.sport?.Title || 'Самбо',
            prizes: coach.attributes?.Achievements || '',
            experience: coach.attributes?.Experience || '',
            image: coach.attributes?.Image?.data?.attributes?.url 
                ? `${coach.attributes.Image.data.attributes.url}` 
                : null,
            position: coach.attributes?.Position || '',
            description: coach.attributes?.Description || '',
            achievements: coach.attributes?.AdditionalAchievements || []
        };
    };

    const getDisplayedCount = () => {
        if (searchTerm.trim()) {
            return displayedCoaches.length;
        }
        return totalCoaches;
    };

    const getResultsText = () => {
        if (searchTerm.trim()) {
            return `Найдено тренеров: ${displayedCoaches.length}`;
        }
        return `Всего тренеров: ${totalCoaches}`;
    };

    return (
        <>
            <Block
                flexDirection='column'
                justifyContent='center'
                alignItems='start'
                width="75%"
                height='fit-content'
                background="rgba(255, 255, 255, 0.85)"
                className={style.blockWrapper}
            >
                <FadeIn>
                    <div className={style.content}>
                        {/* Header Section */}
                        <div className={style.headerSection}>
                            <div className={style.headerContent}>
                                <div className={style.headerLeft}>
                                    <div className={style.headerIcon}>
                                        <FiUsers />
                                    </div>
                                    <div>
                                        <h2 className={style.headerTitle}>Наши тренеры</h2>
                                        <p className={style.headerSubtitle}>
                                            Профессионалы своего дела
                                        </p>
                                    </div>
                                </div>
                                <div className={style.headerStats}>
                                    <div className={style.statItem}>
                                        <span className={style.statNumber}>{totalCoaches}</span>
                                        <span className={style.statLabel}>Тренеров</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Search Section */}
                        <div className={style.searchSection}>
                            <div className={style.searchContainer}>
                                <div className={`${style.searchInputWrapper} ${isSearchFocused ? style.focused : ''}`}>
                                    <FiSearch className={style.searchIcon} />
                                    <input
                                        ref={searchInputRef}
                                        type="text"
                                        value={searchTerm}
                                        onChange={handleSearchChange}
                                        onFocus={() => setIsSearchFocused(true)}
                                        onBlur={() => setIsSearchFocused(false)}
                                        placeholder="Поиск тренеров по ФИО..."
                                        className={style.searchInput}
                                        aria-label="Поиск тренеров по ФИО"
                                        autoComplete="off"
                                    />
                                    {searchTerm && (
                                        <button 
                                            className={style.clearButton}
                                            onClick={handleClearSearch}
                                            aria-label="Очистить поиск"
                                        >
                                            <FiX />
                                        </button>
                                    )}
                                </div>
                                
                                <div className={style.searchStats}>
                                    <span className={style.resultsCount}>
                                        {getResultsText()}
                                    </span>
                                </div>
                            </div>
                        </div>
                        
                        {loading ? (
                            <div className={style.loading}>
                                <div className={style.loadingSpinner}></div>
                                <p>Загрузка тренеров...</p>
                            </div>
                        ) : (
                            <>
                                <div className={style.teamgrid}>
                                    {displayedCoaches.length > 0 ? (
                                        displayedCoaches.map((coach, index) => {
                                            const coachData = prepareCoachDataForCard(coach);
                                            return (
                                                <div 
                                                    key={coach.id} 
                                                    className={style.cardWrapper}
                                                    style={{ animationDelay: `${index * 0.05}s` }}
                                                >
                                                    <TeammateCard
                                                        width="100%"
                                                        name={coachData.name}
                                                        position={coachData.position}
                                                        sport={coachData.sport}
                                                        prizes={coachData.prizes}
                                                        experience={coachData.experience}
                                                        image={coachData.image}
                                                        onClick={() => handleCoachClick(coach)}
                                                    />
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <div className={style.noCoaches}>
                                            <div className={style.noCoachesIcon}>🔍</div>
                                            <h3>Тренеры не найдены</h3>
                                            <p>
                                                {searchTerm 
                                                    ? `По запросу "${searchTerm}" тренеры не найдены` 
                                                    : 'Тренеры не найдены'
                                                }
                                            </p>
                                            {searchTerm && (
                                                <Button
                                                    text="Показать всех тренеров"
                                                    onClick={handleClearSearch}
                                                    fontSize="1rem"
                                                    padding="0.8rem 1.5rem"
                                                    variant="gradient"
                                                    margin="1rem 0 0 0"
                                                    className={style.showAllButton}
                                                />
                                            )}
                                        </div>
                                    )}
                                </div>
                                
                                {pageCount > 1 && !searchTerm && displayedCoaches.length > 0 && (
                                    <div className={style.paginationWrapper}>
                                        <PaginationControl
                                            page={currentPage}
                                            setPage={setCurrentPage}
                                            pageCount={pageCount}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </FadeIn>
                <Modal
                    width='fit-content'
                    active={modal}
                    setActive={setModal}
                    children={
                        <OnlineRecordingForm />
                    }
                />
            </Block>

            <TeammateModal
                teammate={selectedCoach}
                onClose={handleCloseCoachModal}
            />
        </>
    );
};

export default TeamGridBlock;